import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { withQuery } from "@/lib/redirect-query";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { NotFoundPage } from "@/components/site/NotFoundPage";
import { findPatientResult } from "@/lib/patient-results";
import { absoluteUrl } from "@/lib/absolute-url";
import { quickQuoteUrl } from "@/lib/site-core";
import { breadcrumbSchema, safeJsonLd } from "@/lib/structured-data";

const OLD_RESULTS_PAGES: Record<string, string> = {
  sandberg: "/results/ryne-sandberg/",
  andrew: "/results/",
  tony: "/results/",
  jordan: "/results/",
  martin: "/results/",
  eddie: "/results/",
  // Old first-name-and-initial patient pages. Cloudflare analytics, 3 to 6 Oct
  // 2026, answered 404. The pages are gone, so they go to the results index.
  "ash-p": "/results/",
  "gary-p": "/results/",
  "james-l": "/results/",
  "james-w": "/results/",
  "jason-c": "/results/",
  jason: "/results/",
  john: "/results/",
  "richard-p": "/results/",
  "rob-o": "/results/",
  "rolando-t": "/results/",
  "sergio-n": "/results/",
  "shahid-a": "/results/",
  "tom-c-2": "/results/",
  "tommy-s": "/results/",
  // Old celebrity pages. The live page is the one without the suffix.
  "deion-sanders-pl": "/results/deion-sanders/",
  urlacher_video: "/results/brian-urlacher/",
};

// Patient results pages, /results/<slug>/. The celebrity pages under
// /results/ have their own route files, and those match first.
export const Route = createFileRoute("/results/$slug")({
  loader: ({ params, location }) => {
    const patient = findPatientResult(params.slug);
    if (patient) return patient;
    // Old first-name results pages that other sites still link to (Ahrefs,
    // 1 Oct 2026: andrew 17, tony 13, sandberg 12, jordan 8, martin 6 and
    // eddie 5 referring domains).
    const old = OLD_RESULTS_PAGES[params.slug.toLowerCase()];
    if (old) throw redirect({ href: withQuery(old, location.searchStr), statusCode: 301 });
    throw notFound();
  },
  head: ({ loaderData }) => {
    if (!loaderData)
      return {
        meta: [
          { title: "Page Not Found | RESTORE Hair" },
          { name: "robots", content: "noindex, follow" },
        ],
      };
    const url = `https://restorehair.com/results/${loaderData.slug}/`;
    const title = `${loaderData.name} Hair Transplant Results | RESTORE® Hair`;
    const image = absoluteUrl(loaderData.after.src);
    return {
      meta: [
        { title },
        { name: "description", content: loaderData.description },
        { property: "og:title", content: title },
        { property: "og:description", content: loaderData.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:image", content: image },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: loaderData.description },
        { name: "twitter:image", content: image },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: safeJsonLd(
            breadcrumbSchema([
              { name: "Results", path: "/results/" },
              { name: loaderData.name, path: `/results/${loaderData.slug}/` },
            ]),
          ),
        },
      ],
    };
  },
  component: PatientResultPage,
  notFoundComponent: () => <NotFoundPage />,
});

function PatientResultPage() {
  const p = Route.useLoaderData();
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main data-cta-location="results" className="mx-auto max-w-5xl px-4 py-12 lg:py-16">
        <a href="/results/" className="text-sm font-semibold text-primary hover:underline">
          ← All hair transplant results
        </a>
        <header className="mt-6 text-center">
          <p className="text-lg font-bold text-primary">{p.grafts}</p>
          <h1 className="mt-2 text-4xl font-extrabold text-foreground md:text-5xl">{p.name}</h1>
          <p className="mt-2 text-lg italic text-muted-foreground">{p.role}</p>
          <p className="text-lg text-muted-foreground">RESTORE® Client</p>
        </header>
        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          <figure>
            <img src={p.before.src} alt={p.before.alt} className="w-full object-cover" loading="eager" />
            <figcaption className="mt-2 text-center text-sm font-bold uppercase tracking-wide">Before</figcaption>
          </figure>
          <figure>
            <img src={p.after.src} alt={p.after.alt} className="w-full object-cover" loading="eager" />
            <figcaption className="mt-2 text-center text-sm font-bold uppercase tracking-wide">After</figcaption>
          </figure>
        </div>
        <div className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <a
            href={quickQuoteUrl}
            className="bg-primary px-8 py-4 text-base font-bold text-primary-foreground transition hover:opacity-90"
          >
            Get a Quick Quote
          </a>
          <a
            href="/results/"
            className="border border-border px-8 py-4 text-base font-bold text-foreground transition hover:bg-accent"
          >
            See More Results
          </a>
        </div>
      </main>
      <Footer />
    </div>
  );
}
